import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";
import { Icon } from "./Icons";
import { openPrintWindow, esc, num as pnum } from "./printUtils";

const C = {
  dark:     "#1A1A1A",
  blue:     "#1565C0",
  grey:     "#546E7A",
  light:    "#F5F5F5",
  border:   "#E0E0E0",
  text:     "#212121",
  textSub:  "#757575",
  error:    "#B71C1C",
  success:  "#2E7D32",
  bluLight: "#E3F2FD",
  warn:     "#F57F17",
  warnLight:"#FFF8E1",
};
const FONT      = "Roboto, 'Segoe UI', Arial, sans-serif";
const FONT_SIZE = 15;
const GIORNI       = ["lun", "mar", "mer", "gio", "ven", "sab"];
const GIORNI_LABEL = { lun: "Lun", mar: "Mar", mer: "Mer", gio: "Gio", ven: "Ven", sab: "Sab" };

// Valore di una metrica del riepilogo per un reparto (colli_totali è derivato).
function metricaReparto(rr, key) {
  if (key === "colli_totali") return (rr.prodotti ?? 0) + (rr.anticipati ?? 0);
  return rr[key] ?? 0;
}

// ── Costruzione HTML per la stampa ────────────────────────────────────────────
const METRICHE_RIEPILOGO = [
  { key: "fabbisogno",   label: "Fabbisogno totale colli", dash: false },
  { key: "head_count",   label: "Head count necessario",   dash: true  },
  { key: "prodotti",     label: "Colli prodotti",          dash: false },
  { key: "anticipati",   label: "Colli anticipati",        dash: false },
  { key: "colli_totali", label: "Colli totali prodotti",            dash: false },
  { key: "residui",      label: "Colli residui",           dash: false },
];

// Residuo per reparto di una card (fabbisogno - somma giorni).
function residuoReparto(card, r) {
  const fab = card.per_reparto[String(r.id)]?.fabbisogno ?? 0;
  const prod = GIORNI.reduce((s, g) => s + (card.per_reparto[String(r.id)]?.giorni?.[g] ?? 0), 0);
  return fab - prod;
}

function cardTableHtml(card, reparti) {
  const head = `<tr><th class="lbl"></th><th class="tot">Totale</th>${reparti.map(r => `<th>${esc(r.nome)}</th>`).join("")}</tr>`;

  const totFab = reparti.reduce((s, r) => s + (card.per_reparto[String(r.id)]?.fabbisogno ?? 0), 0);
  const fabRow = `<tr><td class="lbl">Fab.</td><td class="tot">${totFab > 0 ? pnum(totFab) : "—"}</td>${reparti.map(r => {
    const f = card.per_reparto[String(r.id)]?.fabbisogno ?? 0;
    return `<td>${f > 0 ? pnum(f) : "—"}</td>`;
  }).join("")}</tr>`;

  const totRes = reparti.reduce((s, r) => s + residuoReparto(card, r), 0);
  const resRow = `<tr><td class="lbl">Res.</td><td class="tot${totRes <= 0 ? " neg" : ""}">${pnum(totRes)}</td>${reparti.map(r => {
    const v = residuoReparto(card, r);
    return `<td class="${v <= 0 ? "neg" : ""}">${pnum(v)}</td>`;
  }).join("")}</tr>`;

  const giorniRows = GIORNI.map(g => {
    const tot = reparti.reduce((s, r) => s + (card.per_reparto[String(r.id)]?.giorni?.[g] ?? 0), 0);
    return `<tr><td class="lbl">${GIORNI_LABEL[g]}</td><td class="tot">${pnum(tot)}</td>${reparti.map(r => {
      const v = card.per_reparto[String(r.id)]?.giorni?.[g] ?? 0;
      return `<td>${pnum(v)}</td>`;
    }).join("")}</tr>`;
  }).join("");

  const titolo = `${esc(card.commessa_codice)} — ${esc(card.container)}${card.is_anticipo ? " · ANTICIPO" : ""}`;
  return `<div class="block"><h2>${titolo}</h2><table><thead>${head}</thead><tbody>${fabRow}${resRow}${giorniRows}</tbody></table></div>`;
}

function riepilogoTableHtml(data, reparti) {
  const rrList = data.riepilogo_reparti || [];
  const head = `<tr><th class="lbl"></th><th class="tot">Totale</th>${reparti.map(r => `<th>${esc(r.nome)}</th>`).join("")}</tr>`;
  const rows = METRICHE_RIEPILOGO.map(m => {
    const tot = rrList.reduce((s, rr) => s + metricaReparto(rr, m.key), 0);
    return `<tr><td class="lbl">${esc(m.label)}</td><td class="tot">${pnum(tot, { dash: m.dash })}</td>${rrList.map(rr => {
      const v = metricaReparto(rr, m.key);
      return `<td>${pnum(v, { dash: m.dash })}</td>`;
    }).join("")}</tr>`;
  }).join("");
  return `<div class="block"><h2>Pianificazione</h2><table><thead>${head}</thead><tbody>${rows}</tbody></table></div>`;
}

// Calcola, per una riga di import e il giorno scelto, i colli da registrare e l'eventuale conflitto.
function calcRigaImport(r, giorno) {
  const giorni     = r.giorni || {};
  const cellOld    = giorni[giorno] ?? 0;
  const sumGiorni  = GIORNI.reduce((s, g) => s + (giorni[g] ?? 0), 0);
  const resCorrente = (r.fabbisogno ?? 0) - sumGiorni;   // residuo mostrato in UI
  const resEscl    = resCorrente + cellOld;              // residuo escludendo il giorno scelto
  const produced   = resEscl - r.residua_excel;          // produzione attribuita al giorno
  return { cellOld, resCorrente, produced, conflict: cellOld !== 0 };
}

// ── Cella numerica editabile ──────────────────────────────────────────────────
function NumCell({ value, onChange, readOnly = false }) {
  const [local, setLocal] = useState(String(value ?? 0));
  const [editing, setEditing] = useState(false);
  // Sblocco valido SOLO per questa cella: dopo il primo OK non richiede più conferma qui.
  const unlocked = useRef(false);
  useEffect(() => { if (!editing) setLocal(String(value ?? 0)); }, [value, editing]);

  const cellStyle = {
    width: "100%", boxSizing: "border-box", border: "none", background: "transparent",
    textAlign: "center", fontSize: 19, fontWeight: 600, padding: "6px 6px",
    outline: "none", fontFamily: FONT, color: C.text,
  };

  // In sola lettura (o ruolo senza permessi): mostra solo il valore, non editabile.
  if (readOnly) {
    return (
      <div style={{ ...cellStyle, minHeight: 24, lineHeight: "24px", cursor: "default" }}>
        {value ?? 0}
      </div>
    );
  }

  // In sola lettura: div cliccabile. Una cella già compilata (≠0) chiede conferma
  // la PRIMA volta; dopo l'OK questa cella resta sbloccata (le altre no).
  if (!editing) {
    return (
      <div
        onClick={() => {
          if ((value ?? 0) !== 0 && !unlocked.current) {
            if (!window.confirm("Questa cella contiene già un valore. Vuoi modificarla?")) return;
            unlocked.current = true;
          }
          setLocal(String(value ?? 0));
          setEditing(true);
        }}
        style={{ ...cellStyle, cursor: "pointer", minHeight: 24, lineHeight: "24px" }}
      >
        {value ?? 0}
      </div>
    );
  }

  return (
    <input
      type="number"
      autoFocus
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        setEditing(false);
        const v = parseFloat(local) || 0;
        if (v !== (value ?? 0)) onChange(v);
      }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      style={cellStyle}
    />
  );
}

// ── Scheda container ──────────────────────────────────────────────────────────
function ContainerCard({ card, reparti, weekId, onConsuntivoChange, onUploadExcel, onPrint, puoModificare = true }) {
  const isAnticipo = card.is_anticipo;
  const fileInputRef = useRef(null);

  return (
    <div style={{
      background: "#fff",
      border: `1.5px solid ${isAnticipo ? C.warn : C.border}`,
      borderRadius: 8,
      marginBottom: 12,
      overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    }}>
      {/* Header card */}
      <div style={{
        background: isAnticipo ? C.warnLight : C.grey,
        color: isAnticipo ? C.warn : "#fff",
        padding: "8px 14px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>
          {card.commessa_codice} — {card.container}
        </span>
        {isAnticipo && (
          <span style={{
            fontSize: 11, fontWeight: 700, background: C.warn, color: "#fff",
            borderRadius: 10, padding: "2px 8px", letterSpacing: 0.3,
          }}>
            ANTICIPO · {card.week_nome}
          </span>
        )}
        {puoModificare && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) onUploadExcel(card, f);
            e.target.value = "";
          }}
        />
        )}
        {puoModificare && (
        <button
          onClick={() => fileInputRef.current?.click()}
          title="Carica file Excel colli prodotti per questo container"
          style={{
            marginLeft: "auto", cursor: "pointer",
            background: isAnticipo ? C.warn : "#fff",
            color: isAnticipo ? "#fff" : C.grey,
            border: "none", borderRadius: 6, padding: "5px 12px",
            fontSize: 13, fontWeight: 700, fontFamily: FONT,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <Icon name="upload" size={15} /> Carica Excel
        </button>
        )}
        <button
          onClick={() => onPrint?.(card)}
          title="Stampa questa tabella"
          style={{
            marginLeft: puoModificare ? undefined : "auto",
            cursor: "pointer",
            background: isAnticipo ? C.warn : "#fff",
            color: isAnticipo ? "#fff" : C.grey,
            border: "none", borderRadius: 6, padding: "5px 12px",
            fontSize: 13, fontWeight: 700, fontFamily: FONT,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <Icon name="print" size={15} /> Stampa
        </button>
      </div>

      {/* Mini-tabella */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 16, width: "100%", fontFamily: FONT }}>
          <thead>
            <tr style={{ background: C.light }}>
              <th style={{ ...thSt, width: 52, textAlign: "left", paddingLeft: 10 }}></th>
              <th style={{ ...thSt, minWidth: 80, borderLeft: `3px solid ${C.grey}`, background: C.bluLight, color: C.blue }}>
                Totale
              </th>
              {reparti.map(r => (
                <th key={r.id} style={{ ...thSt, minWidth: 80, borderLeft: `2px solid ${C.border}` }}>
                  {r.nome}
                </th>
              ))}
            </tr>
            {/* Riga fabbisogno */}
            <tr style={{ background: "#FAFAFA" }}>
              <td style={{ ...tdSt, fontWeight: 700, color: C.textSub, paddingLeft: 10, fontSize: 12 }}>Fab.</td>
              {(() => {
                const totFab = reparti.reduce((s, r) => s + (card.per_reparto[String(r.id)]?.fabbisogno ?? 0), 0);
                return (
                  <td style={{ ...tdSt, textAlign: "center", fontWeight: 700, fontSize: 18, borderLeft: `3px solid ${C.grey}`, background: C.bluLight, color: totFab > 0 ? C.blue : C.textSub }}>
                    {totFab > 0 ? totFab.toLocaleString("it-IT") : "—"}
                  </td>
                );
              })()}
              {reparti.map(r => {
                const fab = card.per_reparto[String(r.id)]?.fabbisogno ?? 0;
                return (
                  <td key={r.id} style={{ ...tdSt, textAlign: "center", fontWeight: 700, fontSize: 18, borderLeft: `2px solid ${C.border}`, color: fab > 0 ? C.text : C.textSub }}>
                    {fab > 0 ? fab.toLocaleString("it-IT") : "—"}
                  </td>
                );
              })}
            </tr>
            {/* Riga residuo da produrre */}
            <tr style={{ background: "#F0F4F8" }}>
              <td style={{ ...tdSt, fontWeight: 700, color: C.textSub, paddingLeft: 10, fontSize: 12 }}>Res.</td>
              {(() => {
                const totRes = reparti.reduce((s, r) => {
                  const fab      = card.per_reparto[String(r.id)]?.fabbisogno ?? 0;
                  const prodotti = GIORNI.reduce((a, g) => a + (card.per_reparto[String(r.id)]?.giorni?.[g] ?? 0), 0);
                  return s + (fab - prodotti);
                }, 0);
                const color = totRes <= 0 ? C.success : C.blue;
                return (
                  <td style={{ ...tdSt, textAlign: "center", fontWeight: 700, fontSize: 18, borderLeft: `3px solid ${C.grey}`, background: totRes <= 0 ? "#E8F5E9" : C.bluLight, color }}>
                    {totRes.toLocaleString("it-IT")}
                  </td>
                );
              })()}
              {reparti.map(r => {
                const fab      = card.per_reparto[String(r.id)]?.fabbisogno ?? 0;
                const prodotti = GIORNI.reduce((s, g) => s + (card.per_reparto[String(r.id)]?.giorni?.[g] ?? 0), 0);
                const res      = fab - prodotti;
                const color    = res <= 0 ? C.success : C.text;
                const bgCell   = res <= 0 ? "#E8F5E9" : "inherit";
                return (
                  <td key={r.id} style={{ ...tdSt, textAlign: "center", fontWeight: 700, fontSize: 18, borderLeft: `2px solid ${C.border}`, color, background: bgCell }}>
                    {res.toLocaleString("it-IT")}
                  </td>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {GIORNI.map((g, gi) => (
              <tr key={g} style={{ background: gi % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                <td style={{ ...tdSt, fontWeight: 600, paddingLeft: 10, color: C.grey, fontSize: 13 }}>
                  {GIORNI_LABEL[g]}
                </td>
                {(() => {
                  const totGiorno = reparti.reduce((s, r) => s + (card.per_reparto[String(r.id)]?.giorni?.[g] ?? 0), 0);
                  return (
                    <td style={{ ...tdSt, textAlign: "center", fontWeight: 700, fontSize: 18, borderLeft: `3px solid ${C.grey}`, background: C.bluLight, color: C.blue }}>
                      {totGiorno.toLocaleString("it-IT")}
                    </td>
                  );
                })()}
                {reparti.map(r => {
                  const val = card.per_reparto[String(r.id)]?.giorni?.[g] ?? 0;
                  return (
                    <td key={r.id} style={{ ...tdSt, padding: 0, borderLeft: `2px solid ${C.border}` }}>
                      <NumCell
                        value={val}
                        onChange={v => onConsuntivoChange(card.commessa_id, card.container, r.id, g, v)}
                        readOnly={!puoModificare}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thSt = { padding: "8px 8px", fontWeight: 700, textAlign: "center", borderBottom: `2px solid ${C.border}`, fontSize: 16, color: C.grey, background: "inherit" };
const tdSt = { padding: "2px 6px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle", minWidth: 72, height: 36 };

// ── Componente principale ─────────────────────────────────────────────────────
export default function DettaglioConsuntivo() {
  const { canEdit } = useAuth();
  const puoModificare = canEdit("dettaglio");
  const [weeks, setWeeks]   = useState([]);
  const [weekId, setWeekId] = useState(() => {
    const saved = localStorage.getItem("weekId_dettaglio_cons");
    return saved ? parseInt(saved) : null;
  });
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  // Per aggiungere container da altre settimane
  const [tuttiContainers, setTuttiContainers] = useState([]);
  const [showAggiungi, setShowAggiungi]       = useState(false);
  const [containerAggiuntivi, setContainerAggiuntivi] = useState([]); // {commessa_id, commessa_codice, container, week_id, week_nome}

  const saveTimer = useRef({});

  // Import Excel colli prodotti (anteprima + conferma)
  const [importData, setImportData]     = useState(null);   // risposta preview
  const [importCard, setImportCard]     = useState(null);
  const [importGiorno, setImportGiorno] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError]   = useState("");

  useEffect(() => {
    api.getWeeks().then(ws => {
      setWeeks(ws);
      if (ws.length > 0) {
        const saved = localStorage.getItem("weekId_dettaglio_cons");
        const valid = saved && ws.find(w => w.id === parseInt(saved));
        if (!valid) setWeekId(ws[0].id);
      }
    }).catch(e => setError(e.message));
  }, []);

  const handleSetWeekId = (id) => {
    setWeekId(id);
    setContainerAggiuntivi([]);
    localStorage.setItem("weekId_dettaglio_cons", String(id));
  };

  const loadData = useCallback(async (wid) => {
    if (!wid) return;
    setLoading(true);
    try {
      const d = await api.getDettaglioConsuntivo(wid);
      setData(d);
      setError("");
      // Carica lista container aggiungibili
      const tutti = await api.getTuttiContainers(wid);
      setTuttiContainers(tutti);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (weekId) loadData(weekId); }, [weekId, loadData]);

  const handleConsuntivoChange = (commessaId, container, repartoId, giorno, value) => {
    // Aggiorna lo stato locale (ottimistico)
    setData(prev => {
      if (!prev) return prev;
      const containers = prev.containers.map(card => {
        if (card.commessa_id !== commessaId || card.container !== container) return card;
        const pr = { ...card.per_reparto };
        const rid = String(repartoId);
        pr[rid] = { ...pr[rid], giorni: { ...pr[rid]?.giorni, [giorno]: value } };
        return { ...card, per_reparto: pr };
      });
      // Aggiorna anche i container aggiuntivi
      const updated_agg = containerAggiuntivi; // gestiti separatamente sotto
      return { ...prev, containers };
    });

    // Aggiorna anche containerAggiuntivi se è lì
    setContainerAggiuntivi(prev => prev.map(card => {
      if (card.commessa_id !== commessaId || card.container !== container) return card;
      const pr = { ...card.per_reparto };
      const rid = String(repartoId);
      pr[rid] = { ...pr[rid], giorni: { ...pr[rid]?.giorni, [giorno]: value } };
      return { ...card, per_reparto: pr };
    }));

    // Ricalcola riepilogo localmente
    setData(prev => {
      if (!prev) return prev;
      const allCards = [...prev.containers, ...containerAggiuntivi];
      const riepilogo = prev.riepilogo_reparti.map(rr => {
        const prod = allCards.reduce((s, card) => {
          const pr = card.per_reparto[String(rr.reparto_id)];
          return s + GIORNI.reduce((ss, g) => ss + (pr?.giorni?.[g] ?? 0), 0);
        }, 0);
        const deltaCard = allCards.find(c => c.commessa_id === commessaId && c.container === container);
        const oldVal = deltaCard?.per_reparto[String(rr.reparto_id)]?.giorni?.[giorno] ?? 0;
        const prodAggiornato = String(rr.reparto_id) === String(repartoId)
          ? prod - oldVal + value
          : prod;
        // anticipati = somma fabbisogni dei container anticipo (non cambia con i prodotti)
        const ant = allCards
          .filter(c => c.is_anticipo)
          .reduce((s, c) => s + (c.per_reparto[String(rr.reparto_id)]?.fabbisogno ?? 0), 0);
        const res = Math.max(0, rr.fabbisogno - prodAggiornato);
        return { ...rr, prodotti: prodAggiornato, anticipati: ant, residui: res };
      });
      return { ...prev, riepilogo_reparti: riepilogo };
    });

    const key = `cons_${commessaId}_${container}_${repartoId}_${giorno}`;
    if (saveTimer.current[key]) clearTimeout(saveTimer.current[key]);
    saveTimer.current[key] = setTimeout(async () => {
      try {
        await api.upsertConsuntivo({
          commessa_id: commessaId,
          container,
          reparto_id: repartoId,
          week_id: weekId,
          giorno,
          colli_prodotti: value,
        });
        setError("");
      } catch (e) {
        setError("Errore salvataggio: " + e.message);
        loadData(weekId);
      }
    }, 400);
  };

  const handleUploadExcel = async (card, file) => {
    setImportLoading(true);
    setImportError("");
    setImportCard(card);
    try {
      const res = await api.importExcelPreview({
        file,
        commessa_id: card.commessa_id,
        container: card.container,
        week_id: weekId,
      });
      setImportData(res);
      setImportGiorno(res.giorno_default || "lun");
    } catch (e) {
      setImportError(e.message);
      setImportData(null);
      setImportCard(null);
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfermaImport = async (modo = "sovrascrivi") => {
    if (!importData || !importCard || !importGiorno) return;
    if (importData.commessa_mismatch) {
      const ok = window.confirm(
        `Attenzione: il file sembra della commessa ${importData.commessa_excel}, ` +
        `ma stai caricando su ${importData.codice_commessa}. Procedere comunque?`
      );
      if (!ok) return;
    }
    setImportLoading(true);
    try {
      await api.importExcelApply({
        commessa_id: importCard.commessa_id,
        container: importCard.container,
        week_id: weekId,
        giorno: importGiorno,
        modo,
        righe: importData.righe.map(r => ({
          reparto_id: r.reparto_id,
          residua_excel: r.residua_excel,
        })),
      });
      setImportData(null);
      setImportCard(null);
      await loadData(weekId);
    } catch (e) {
      setImportError(e.message);
    } finally {
      setImportLoading(false);
    }
  };

  const chiudiImport = () => {
    setImportData(null);
    setImportCard(null);
    setImportError("");
  };

  const handleAggiungiContainer = (selected) => {
    if (!selected || !data) return;
    const [commessaId, container] = selected.split("||");
    const found = tuttiContainers.find(c => c.commessa_id === parseInt(commessaId) && c.container === container);
    if (!found) return;

    // Controlla che non sia già presente
    const giaPresente = [
      ...(data.containers || []),
      ...containerAggiuntivi,
    ].some(c => c.commessa_id === found.commessa_id && c.container === found.container);
    if (giaPresente) { setShowAggiungi(false); return; }

    const per_reparto = {};
    (data.reparti || []).forEach(r => {
      per_reparto[String(r.id)] = { fabbisogno: 0, giorni: Object.fromEntries(GIORNI.map(g => [g, 0])) };
    });
    setContainerAggiuntivi(prev => [...prev, {
      ...found,
      is_anticipo: true,
      per_reparto,
    }]);
    setShowAggiungi(false);
  };

  const allCards = data ? [...(data.containers || []), ...containerAggiuntivi] : [];
  const reparti  = data?.reparti || [];
  const weekNome = weeks.find(w => w.id === weekId)?.nome || "";

  const handlePrintCard = (card) => {
    openPrintWindow(
      `${card.commessa_codice} — ${card.container} · ${weekNome}`,
      cardTableHtml(card, reparti),
    );
  };

  const handlePrintWeek = () => {
    if (!data) return;
    const body = riepilogoTableHtml(data, reparti) + allCards.map(c => cardTableHtml(c, reparti)).join("");
    openPrintWindow(`Dettaglio / Consuntivo · ${weekNome}`, body);
  };

  return (
    <div style={{ fontFamily: FONT, background: C.light, minHeight: "100vh" }}>
      <div style={{ padding: 20 }}>

        {/* Selettore settimana */}
        <div style={{
          background: "#fff", borderRadius: 8, padding: "12px 16px",
          marginBottom: 16, border: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.8 }}>
            Settimana
          </label>
          <select
            value={weekId ?? ""}
            onChange={e => handleSetWeekId(parseInt(e.target.value))}
            style={{
              border: `1.5px solid ${C.blue}`, borderRadius: 6, padding: "8px 14px",
              fontSize: FONT_SIZE, outline: "none", cursor: "pointer",
              background: "#fff", color: C.blue, fontWeight: 700, fontFamily: FONT,
            }}
          >
            {weeks.map(w => <option key={w.id} value={w.id}>{w.nome}</option>)}
          </select>
          <button
            onClick={handlePrintWeek}
            disabled={!data}
            title="Stampa il riepilogo e tutti i container della settimana selezionata"
            style={{
              marginLeft: "auto", cursor: data ? "pointer" : "default",
              background: data ? C.blue : "#B0BEC5", color: "#fff", border: "none", borderRadius: 6,
              padding: "8px 16px", fontSize: 14, fontWeight: 700, fontFamily: FONT,
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <Icon name="print" size={15} /> Stampa settimana
          </button>
        </div>

        {/* Banner errore */}
        {error && (
          <div style={{
            background: "#FFEBEE", border: `1px solid #EF9A9A`, color: C.error,
            padding: "10px 14px", borderRadius: 6, marginBottom: 12,
            fontSize: FONT_SIZE, display: "flex", alignItems: "center", fontWeight: 600,
          }}>
            <Icon name="warning" size={16} color={C.error} style={{ marginRight: 6 }} />{error}
            <button onClick={() => setError("")} style={{ marginLeft: "auto", cursor: "pointer", background: "none", border: "none", fontWeight: 700, fontSize: 18, color: C.error }}>×</button>
          </div>
        )}

        {loading && <div style={{ padding: 60, textAlign: "center", color: C.textSub, fontSize: FONT_SIZE }}>Caricamento...</div>}

        {!loading && data && (
          <>
            {/* ── Riepilogo per reparto ─────────────────────────────────────── */}
            {reparti.length > 0 && (
              <div style={{
                background: "#fff", borderRadius: 8, marginBottom: 20,
                border: `1px solid ${C.border}`, overflow: "hidden",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              }}>
                <div style={{ background: C.grey, color: "#fff", padding: "10px 16px", fontWeight: 700, fontSize: 15 }}>
                  Pianificazione
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: FONT_SIZE, fontFamily: FONT }}>
                    <thead>
                      <tr style={{ background: C.light }}>
                        <th style={{ ...thSt, textAlign: "left", paddingLeft: 16, minWidth: 200 }}></th>
                        <th style={{ ...thSt, minWidth: 100, borderLeft: `3px solid ${C.grey}`, background: C.bluLight, color: C.blue }}>
                          Totale
                        </th>
                        {reparti.map(r => (
                          <th key={r.id} style={{ ...thSt, minWidth: 100, borderLeft: `2px solid ${C.border}` }}>
                            {r.nome}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: "fabbisogno",   label: "Fabbisogno totale colli", bold: true,  color: C.text },
                        { key: "head_count",   label: "Head count necessario",   bold: false, color: C.textSub, format: v => v > 0 ? v.toLocaleString("it-IT") : "—" },
                        { key: "prodotti",     label: "Colli prodotti",   bold: false, color: C.text },
                        { key: "anticipati",   label: "Colli anticipati", bold: false, color: C.warn },
                        { key: "colli_totali", label: "Colli totali prodotti",     bold: true,  color: C.text },
                        { key: "residui",      label: "Colli residui",    bold: true,  color: null },
                      ].map(({ key, label, bold, color, format }, i) => {
                        const reparti_rr = data.riepilogo_reparti || [];
                        const totale = reparti_rr.reduce((s, rr) => s + metricaReparto(rr, key), 0);
                        const cellColorFor = (val) => {
                          let cc = color;
                          if (key === "residui")   cc = val > 0 ? C.text : C.success;
                          if (key === "anticipati") cc = val > 0 ? C.warn : C.textSub;
                          return cc;
                        };
                        const displayFor = (val) => format ? format(val) : (val !== 0 ? val.toLocaleString("it-IT") : (key === "anticipati" ? "—" : "0"));
                        return (
                        <tr key={key} style={{ background: i % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                          <td style={{
                            padding: "12px 16px", fontWeight: bold ? 700 : 500,
                            borderBottom: `1px solid ${C.border}`, fontSize: 17,
                            color: C.text,
                          }}>
                            {label}
                          </td>
                          <td style={{
                            padding: "12px 8px", textAlign: "center",
                            fontWeight: 700, borderBottom: `1px solid ${C.border}`,
                            borderLeft: `3px solid ${C.grey}`, fontSize: 21,
                            background: C.bluLight, color: cellColorFor(totale),
                          }}>
                            {displayFor(totale)}
                          </td>
                          {reparti_rr.map(rr => {
                            const val = metricaReparto(rr, key);
                            return (
                              <td key={rr.reparto_id} style={{
                                padding: "12px 8px", textAlign: "center",
                                fontWeight: bold ? 700 : 600, borderBottom: `1px solid ${C.border}`,
                                borderLeft: `2px solid ${C.border}`, fontSize: 21,
                                color: cellColorFor(val),
                              }}>
                                {displayFor(val)}
                              </td>
                            );
                          })}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Schede container ──────────────────────────────────────────── */}
            {allCards.length === 0 && (
              <div style={{
                padding: 40, textAlign: "center", color: C.textSub,
                background: "#fff", borderRadius: 8, border: `1px solid ${C.border}`,
              }}>
                Nessun container pianificato per questa settimana.
              </div>
            )}

            {allCards.map(card => (
              <ContainerCard
                key={`${card.commessa_id}_${card.container}`}
                card={card}
                reparti={reparti}
                weekId={weekId}
                onConsuntivoChange={handleConsuntivoChange}
                onUploadExcel={handleUploadExcel}
                onPrint={handlePrintCard}
                puoModificare={puoModificare}
              />
            ))}

            {/* ── Aggiungi container da altra settimana ─────────────────────── */}
            {puoModificare && (
            <div style={{ marginTop: 8 }}>
              {!showAggiungi ? (
                <button
                  onClick={() => setShowAggiungi(true)}
                  disabled={tuttiContainers.length === 0}
                  style={{
                    background: "#fff", border: `1.5px dashed ${C.border}`, borderRadius: 8,
                    padding: "12px 20px", cursor: tuttiContainers.length === 0 ? "default" : "pointer",
                    fontSize: 14, color: C.textSub, fontFamily: FONT, width: "100%",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  + Aggiungi container da altra settimana (anticipo)
                </button>
              ) : (
                <div style={{
                  background: "#fff", border: `1.5px solid ${C.blue}`, borderRadius: 8,
                  padding: "14px 16px", display: "flex", gap: 10, alignItems: "center",
                }}>
                  <select
                    defaultValue=""
                    onChange={e => handleAggiungiContainer(e.target.value)}
                    style={{
                      border: `1.5px solid ${C.blue}`, borderRadius: 6, padding: "8px 12px",
                      fontSize: FONT_SIZE, fontFamily: FONT, flex: 1, outline: "none",
                    }}
                  >
                    <option value="">— Seleziona container —</option>
                    {tuttiContainers.map(c => (
                      <option key={`${c.commessa_id}_${c.container}`} value={`${c.commessa_id}||${c.container}`}>
                        {c.commessa_codice} — {c.container} ({c.week_nome})
                      </option>
                    ))}
                  </select>
                  <button onClick={() => setShowAggiungi(false)}
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontFamily: FONT, color: C.textSub }}>
                    Annulla
                  </button>
                </div>
              )}
            </div>
            )}
          </>
        )}
      </div>

      {/* ── Modale import Excel ──────────────────────────────────────────────── */}
      {(importData || importLoading) && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 10, width: "100%", maxWidth: 760,
            maxHeight: "90vh", display: "flex", flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)", fontFamily: FONT,
          }}>
            <div style={{ background: C.grey, color: "#fff", padding: "14px 20px", borderRadius: "10px 10px 0 0", fontWeight: 700, fontSize: 16 }}>
              Import colli prodotti {importCard ? `· ${importCard.commessa_codice} — ${importCard.container}` : ""}
            </div>

            <div style={{ padding: 20, overflowY: "auto" }}>
              {importLoading && !importData && (
                <div style={{ padding: 30, textAlign: "center", color: C.textSub }}>Lettura file…</div>
              )}

              {importError && (
                <div style={{ background: "#FFEBEE", border: "1px solid #EF9A9A", color: C.error, padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontWeight: 600 }}>
                  <Icon name="warning" size={16} color={C.error} style={{ marginRight: 6 }} />{importError}
                </div>
              )}

              {importData && (
                <>
                  {/* Avviso commessa errata */}
                  {importData.commessa_mismatch && (
                    <div style={{
                      background: "#FFEBEE", border: `2px solid ${C.error}`, borderRadius: 6,
                      padding: "12px 14px", marginBottom: 16, fontSize: 14, color: C.error, fontWeight: 600,
                    }}>
                      <Icon name="block" size={16} color={C.error} style={{ marginRight: 6 }} /><b>Commessa diversa!</b> Il file sembra riferito alla commessa <b>{importData.commessa_excel}</b>,
                      ma stai caricando su <b>{importData.codice_commessa}</b> (commessa {importData.commessa_attesa}).
                      <br/>Verifica di aver scelto il container giusto prima di registrare.
                    </div>
                  )}

                  {/* Selettore giorno */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 700, color: C.textSub }}>Registra sul giorno</label>
                    <select
                      value={importGiorno ?? ""}
                      onChange={e => setImportGiorno(e.target.value)}
                      style={{ border: `1.5px solid ${C.blue}`, borderRadius: 6, padding: "6px 12px", fontSize: FONT_SIZE, fontWeight: 700, color: C.blue, fontFamily: FONT, outline: "none", cursor: "pointer" }}
                    >
                      {(importData.giorni || GIORNI).map(g => (
                        <option key={g} value={g}>{GIORNI_LABEL[g] || g}</option>
                      ))}
                    </select>
                    {!importData.giorno_default && (
                      <span style={{ fontSize: 12, color: C.warn, fontWeight: 600 }}>oggi è domenica: scegli un giorno</span>
                    )}
                  </div>

                  {/* Tabella anteprima */}
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: C.light }}>
                        <th style={{ ...thSt, textAlign: "left", paddingLeft: 10 }}>Reparto</th>
                        <th style={{ ...thSt }}>Res. attuale</th>
                        <th style={{ ...thSt }}>Residua Excel</th>
                        <th style={{ ...thSt }}>Già su {GIORNI_LABEL[importGiorno] || "—"}</th>
                        <th style={{ ...thSt }}>Da registrare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importData.righe.length === 0 && (
                        <tr><td colSpan={5} style={{ ...tdSt, textAlign: "center", color: C.textSub, padding: 16 }}>Nessun reparto corrispondente nel file.</td></tr>
                      )}
                      {importData.righe.map(r => {
                        const { cellOld, resCorrente, produced, conflict } = calcRigaImport(r, importGiorno);
                        const pos = produced > 0, neg = produced < 0;
                        return (
                          <tr key={r.reparto_id} style={conflict ? { background: C.warnLight } : undefined}>
                            <td style={{ ...tdSt, paddingLeft: 10, fontWeight: 600 }}>{r.reparto_nome}</td>
                            <td style={{ ...tdSt, textAlign: "center" }}>{resCorrente.toLocaleString("it-IT")}</td>
                            <td style={{ ...tdSt, textAlign: "center" }}>{r.residua_excel.toLocaleString("it-IT")}</td>
                            <td style={{ ...tdSt, textAlign: "center", fontWeight: conflict ? 700 : 400, color: conflict ? C.warn : C.textSub }}>
                              {cellOld.toLocaleString("it-IT")}
                            </td>
                            <td style={{ ...tdSt, textAlign: "center", fontWeight: 700, fontSize: 16, color: neg ? C.error : (pos ? C.success : C.textSub) }}>
                              {produced > 0 ? "+" : ""}{produced.toLocaleString("it-IT")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Reparti ignorati */}
                  {importData.ignorati.length > 0 && (
                    <div style={{ marginTop: 16, background: C.warnLight, border: `1px solid ${C.warn}`, borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#8a6d00" }}>
                      <b>Ignorati</b> (nessun reparto corrispondente): {importData.ignorati.map(i => `${i.codice} (${i.linea})`).join(", ")}
                    </div>
                  )}

                  {/* Avviso conflitto: il giorno scelto ha già dati */}
                  {(() => {
                    const inConflitto = importData.righe.filter(r => calcRigaImport(r, importGiorno).conflict);
                    if (inConflitto.length === 0) return null;
                    return (
                      <div style={{ marginTop: 16, background: "#FFF3E0", border: `1.5px solid ${C.warn}`, borderRadius: 6, padding: "12px 14px", fontSize: 14, color: "#8a4b00" }}>
                        <Icon name="warning" size={15} color="#8a4b00" style={{ marginRight: 4 }} />Su <b>{GIORNI_LABEL[importGiorno]}</b> ci sono già dati per: <b>{inConflitto.map(r => r.reparto_nome).join(", ")}</b>.<br/>
                        Scegli come procedere: <b>Somma</b> mantiene i valori esistenti e aggiunge la produzione del file; <b>Sovrascrivi</b> sostituisce con la produzione del file.
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={chiudiImport} disabled={importLoading}
                style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 18px", cursor: "pointer", fontFamily: FONT, color: C.textSub, fontWeight: 600 }}>
                Annulla
              </button>
              {(() => {
                const disabled = importLoading || !importData || importData.righe.length === 0 || !importGiorno;
                const hasConflict = importData && importData.righe.some(r => calcRigaImport(r, importGiorno).conflict);
                if (hasConflict) {
                  return (
                    <>
                      <button onClick={() => handleConfermaImport("somma")} disabled={disabled}
                        style={{ background: C.grey, color: "#fff", border: "none", borderRadius: 6, padding: "9px 20px", cursor: importLoading ? "default" : "pointer", fontFamily: FONT, fontWeight: 700, opacity: disabled ? 0.5 : 1 }}>
                        {importLoading ? "…" : "Somma le quantità"}
                      </button>
                      <button onClick={() => handleConfermaImport("sovrascrivi")} disabled={disabled}
                        style={{ background: C.blue, color: "#fff", border: "none", borderRadius: 6, padding: "9px 20px", cursor: importLoading ? "default" : "pointer", fontFamily: FONT, fontWeight: 700, opacity: disabled ? 0.5 : 1 }}>
                        {importLoading ? "…" : "Sovrascrivi"}
                      </button>
                    </>
                  );
                }
                return (
                  <button onClick={() => handleConfermaImport("sovrascrivi")} disabled={disabled}
                    style={{ background: C.blue, color: "#fff", border: "none", borderRadius: 6, padding: "9px 22px", cursor: importLoading ? "default" : "pointer", fontFamily: FONT, fontWeight: 700, opacity: disabled ? 0.5 : 1 }}>
                    {importLoading ? "Registro…" : "Conferma e registra"}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
